import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Car, Loader2, CheckCircle, Clock, MapPin, RefreshCw, AlertCircle, Shield, Send
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ValetJourneyAnimation from "@/components/ValetJourneyAnimation";
import PushPrompt from "@/components/PushPrompt";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  parked: { label: "Parked", color: "text-blue-400", bg: "bg-blue-500/10", icon: MapPin, description: "Your car is safely parked. Tap below when you're ready to leave." },
  requested: { label: "Requested", color: "text-amber-400", bg: "bg-amber-500/10", icon: Clock, description: "Your car has been requested. The valet team is on it!" },
  fetching: { label: "Being Fetched", color: "text-orange-400", bg: "bg-orange-500/10", icon: Car, description: "A valet is bringing your car to the exit right now." },
  ready: { label: "Ready!", color: "text-emerald-400", bg: "bg-emerald-500/10", icon: CheckCircle, description: "Your car is waiting at the exit. Please proceed!" },
  completed: { label: "Complete", color: "text-gray-400", bg: "bg-gray-500/10", icon: CheckCircle, description: "Your visit is complete. Thank you for using VeloPark!" },
};

export default function TrackPage() {
  const { token } = useParams();
  const [guestData, setGuestData] = useState(null);
  const [guestToken, setGuestToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef(null);
  const pushNotif = usePushNotifications({ guestId: guestData?.guest_id });

  const fetchByToken = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/track/${token}`);
      setGuestData(res.data);
      setGuestToken(res.data.guest_token);
      setError(null);
      if (res.data.status === "completed") {
        clearInterval(pollRef.current);
      }
    } catch (err) {
      if (!silent) setError(err.response?.data?.detail || "Invalid or expired tracking link.");
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchByToken();
  }, [fetchByToken]);

  // Auto-poll every 10 seconds
  useEffect(() => {
    if (guestData && guestData.status !== "completed") {
      pollRef.current = setInterval(() => fetchByToken(true), 10000);
      return () => clearInterval(pollRef.current);
    }
  }, [guestData?.status, fetchByToken]);

  const handleRequestCar = async () => {
    if (!guestToken || !guestData) return;
    setRequesting(true);
    try {
      await axios.post(`${API}/guest/${guestData.guest_id}/request-car`, {}, {
        headers: { Authorization: `Bearer ${guestToken}` }
      });
      toast.success("Car requested! The valet team is on it.");
      fetchByToken();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request car");
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex flex-col" data-testid="track-error-page">
        <div className="px-4 py-4 border-b border-white/5">
          <div className="flex items-center justify-center gap-2">
            <img src="/logo.svg" alt="VeloPark" className="w-5 h-5 rounded-full" />
            <span className="text-white font-bold text-lg tracking-wide">VeloPark</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
            <h2 className="text-xl font-bold text-white">Link Not Found</h2>
            <p className="text-gray-400 text-sm max-w-xs mx-auto">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[guestData.status] || STATUS_CONFIG.parked;

  return (
    <div className="min-h-screen bg-[#090A0F] flex flex-col" data-testid="track-page">
      {/* Header */}
      <div className="px-4 py-4 border-b border-white/5">
        <div className="flex items-center justify-center gap-2">
          <img src="/logo.svg" alt="VeloPark" className="w-5 h-5 rounded-full" />
          <span className="text-white font-bold text-lg tracking-wide">VeloPark</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center p-4 pt-6">
        <div className="w-full max-w-sm space-y-5">
          {/* Animation */}
          <ValetJourneyAnimation status={guestData.status} />

          {/* Push notification prompt */}
          <PushPrompt
            onSubscribe={pushNotif.subscribe}
            isSubscribed={pushNotif.isSubscribed}
            supported={pushNotif.supported}
            permission={pushNotif.permission}
          />

          <div className="text-center space-y-1">
            <h2 className="text-xl font-bold text-white">{guestData.guest_name}</h2>
            <p className="text-gray-400 text-sm">{guestData.venue_name}</p>
          </div>

          {/* Status Card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Status</span>
              <span className={`${statusInfo.color} font-semibold text-lg`} data-testid="track-status-label">{statusInfo.label}</span>
            </div>
            <p className="text-gray-300 text-sm">{statusInfo.description}</p>

            {guestData.estimated_wait && (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#D4AF37] shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">
                    Est. wait: {guestData.estimated_wait.estimated_minutes} min
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-gray-500 text-xs mb-1">Car</p>
                <p className="text-white font-medium truncate" data-testid="track-car-details">{guestData.car_details}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-gray-500 text-xs mb-1">Token</p>
                <p className="text-[#D4AF37] font-mono font-bold" data-testid="track-parking-token">{guestData.parking_token}</p>
              </div>
              {guestData.table_number && (
                <div className="bg-white/5 rounded-xl p-3 col-span-2">
                  <p className="text-gray-500 text-xs mb-1">Table</p>
                  <p className="text-purple-400 font-medium" data-testid="track-table-number">{guestData.table_number}</p>
                </div>
              )}
            </div>
          </div>

          {/* Get My Car Button */}
          {guestData.status === "parked" && (
            <Button
              data-testid="track-get-car-btn"
              onClick={handleRequestCar}
              disabled={requesting}
              className="w-full h-14 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-lg shadow-lg shadow-[#D4AF37]/20"
            >
              {requesting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Car className="w-5 h-5 mr-2" />
              )}
              {requesting ? "Requesting..." : "Get My Car"}
            </Button>
          )}

          {guestData.status === "ready" && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 text-center space-y-2">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" />
              <p className="text-emerald-300 font-semibold text-lg">Your car is at the exit!</p>
              <p className="text-gray-400 text-sm">Please head to the pickup area</p>
            </div>
          )}

          {/* Refresh */}
          <button
            onClick={() => fetchByToken()}
            disabled={refreshing}
            className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-[#D4AF37] text-sm transition-colors py-2"
            data-testid="track-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh Status"}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/5 text-center">
        <p className="text-gray-500 text-xs flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          Secured by VeloPark. Your data is private.
        </p>
        <a href="https://velopark.in" target="_blank" rel="noopener noreferrer" className="text-gray-600 text-[10px] hover:text-[#D4AF37] transition-colors">velopark.in</a>
      </div>
    </div>
  );
}
